'use client';
import React, {
  useState,
} from 'react';
import {
  Menu,
  MenuItem,
  IconButton,
  Avatar,
} from '@mui/material';
import {
  useAuth,
} from '@/contexts/AuthContext';
import {
  notEmptyString,
} from '@/utils/misc';
import {
  getDisplayName,
  getInitials,
} from '@/utils/names';
import {
  LogOut,
  ChevronDown,
} from 'lucide-react';
import clsx from 'clsx';

type Props =  {
  avatarText?: string;
  avatarBgColor?: string;
  className?: string;
}

export default function ProfileDropdown({
  avatarBgColor = '#2563eb',
  className,
}: Props) {
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { logout } = useAuth();
  const open = Boolean(anchorEl);

  const displayName = getDisplayName(user?.user?.formattedNames);
  const initials = getInitials(user?.user?.formattedNames) ?? 'P';
  const { email } = user?.user ?? {};
  const { name: organizationName } = user?.organization ?? {};

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className={clsx('rounded-md bg-transparent', className)}>
      <IconButton
        onClick={handleClick}
        className={clsx('!justify-start !pl-2 w-full h-full !rounded-md')}
      >
        <div className="flex items-center gap-1">
          <Avatar
            sx={{
              width: 32,
              height: 32,
              backgroundColor: avatarBgColor,
              fontSize: '12px',
              fontWeight: 500,
              color: '#ffffff',
            }}
          >
            {initials}
          </Avatar>
          <ChevronDown className="w-4 h-4 text-gray-600" />
        </div>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          list: {
            sx: {
              pt: 0,
              pb: 0,
            },
          },
          paper: {
            sx: {
              minWidth: 200,
              borderRadius: 2,
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              border: '1px solid #e5e7eb',
              '& .MuiMenuItem-root': {
                paddingY: '15px',
              },
            },
          },
        }}
      >
        {/* Header with user info */}
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-100 flex items-center gap-3">
          <Avatar
            sx={{
              width: 40,
              height: 40,
              backgroundColor: avatarBgColor,
              fontSize: '14px',
              fontWeight: 600,
              color: '#ffffff',
            }}
          >
            {initials}
          </Avatar>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{notEmptyString(displayName) ? displayName : ''}</p>
            {notEmptyString(email) && <p className="text-xs text-gray-700 truncate">{email}</p>}
            {notEmptyString(organizationName) && <p className="text-xs text-gray-700 truncate">{organizationName}</p>}
          </div>
        </div>

        <MenuItem
          onClick={async () => { handleClose(); await logout(); }}
          sx={{
            fontSize: '14px',
            padding: '0 16px 10px 16px',
            color: '#374151',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            '&:hover': { backgroundColor: '#f9fafb' },
          }}
        >
          <LogOut className="w-4 h-4" />
          Log Out
        </MenuItem>
      </Menu>
    </div>
  );
};
